import { MutableRefObject } from 'react';

import CommonsService from 'features/commons/CommonsService';

import { catchErrorProps } from 'hooks/useErrorCatch';

import { SimpleOptionType } from 'types/filters';

import { mapCitiesResponse, mapStatesResponse } from './mapLocationOptions';

export interface LoadLocationOptionsParams {
  countryValue: string;
  stateValue?: string;
  shouldFetchStates: boolean;
  isCancelled: () => boolean;
  statesRef: MutableRefObject<SimpleOptionType[]>;
  loadedCountryRef: MutableRefObject<string | undefined>;
  setStates: (states: SimpleOptionType[]) => void;
  setCities: (cities: SimpleOptionType[]) => void;
  setIsLoadingStates: (loading: boolean) => void;
  setIsLoadingCities: (loading: boolean) => void;
  catchError: (props: catchErrorProps) => void;
  errorMessages: {
    fetchStates: string;
    fetchCities: string;
  };
}

export const loadLocationOptions = async ({
  countryValue,
  stateValue,
  shouldFetchStates,
  isCancelled,
  statesRef,
  loadedCountryRef,
  setStates,
  setCities,
  setIsLoadingStates,
  setIsLoadingCities,
  catchError,
  errorMessages
}: LoadLocationOptionsParams): Promise<void> => {
  let currentStates = statesRef.current;

  if (shouldFetchStates) {
    loadedCountryRef.current = countryValue;
    statesRef.current = [];
    setStates([]);
    setCities([]);

    try {
      setIsLoadingStates(true);
      const response = await CommonsService.getStatesByCountry(countryValue);
      if (isCancelled()) {
        return;
      }

      currentStates = mapStatesResponse(response);
      statesRef.current = currentStates;
      setStates(currentStates);
    } catch (error) {
      catchError({
        error,
        default: { message: errorMessages.fetchStates }
      });
      return;
    } finally {
      if (!isCancelled()) {
        setIsLoadingStates(false);
      }
    }
  }

  try {
    setIsLoadingCities(true);

    if (stateValue) {
      const response = await CommonsService.getCitiesByState(stateValue);
      if (isCancelled()) {
        return;
      }
      setCities(mapCitiesResponse(response));
    } else if (currentStates.length === 0) {
      const response = await CommonsService.getCitiesByCountry(countryValue);
      if (isCancelled()) {
        return;
      }
      setCities(mapCitiesResponse(response));
    } else {
      setCities([]);
    }
  } catch (error) {
    catchError({
      error,
      default: { message: errorMessages.fetchCities }
    });
  } finally {
    if (!isCancelled()) {
      setIsLoadingCities(false);
    }
  }
};
